import { useState } from 'react';
import { useSelector } from 'react-redux';
import { FormikProps, useFormik } from 'formik';
import { SendInviteEmailsBody } from '../../commonTypes/organization.types';
import { useContainer, useHttpGetJoinToken, useHttpPostSendEmails, useOrganizationId } from '../../hooks';
import { RootState } from '../../store';
import { Button, IconButton, InputFormik, PremiumTooltip } from '../Common';
import { INVITE_MEMBERS_VALIDATION } from './inviteMembersSchema';
import { GlobalApi } from '../../util/Global';
import { useTranslation } from '../../hooks/useTranslation';
import { useCanCreate } from '../../hooks/features';
import { Feature } from '@cyberlabrs/helpers';
import useHttpGetUsers from '../../hooks/user/userApi/useHttpGetUsers';

interface IInviteMembersProps {
    close: () => void;
    userCount?: number;
}

interface IInviteMembersValues {
    email: string;
}

const EMPTY_FORM_VALUES: IInviteMembersValues = {
    email: '',
};

const InviteMembers = ({ close, userCount }: IInviteMembersProps) => {
    const { t } = useTranslation();
    const organizationId = useOrganizationId();
    const content = useSelector((state: RootState) => state.language.data);
    const { getToken } = useHttpGetJoinToken();
    const globalApi: GlobalApi = useContainer(GlobalApi);
    const { data: users } = useHttpGetUsers(organizationId);
    const actualUserCount = userCount !== undefined ? userCount : users.length;
    const canCreate = useCanCreate(Feature.USERS, 'create', actualUserCount);

    const [emails, setEmails] = useState<string[]>([]);
    const [message, setMessage] = useState(content.invitePeople.linkToInvite);

    const { exec: execSendInviteEmails } = useHttpPostSendEmails();

    const isUnique = (email: string) => {
        if (emails.length === 0) return true;
        return !emails.find(_email => _email === email);
    };

    const copyLink = async () => {
        getToken(
            {},
            {
                onSuccess: token => {
                    const link = `${globalApi.origin()}/join-organization/${token}`;
                    setMessage('Team invite link has been copied');
                    setTimeout(() => setMessage(t('invitePeople.linkToInvite')), 3000);
                    void navigator.clipboard.writeText(link);
                },
            }
        );
    };

    const removeEmailFromList = (_email: string) => {
        setEmails(emails.filter(email => email !== _email));
    };

    const renderIcon = (formikContext: FormikProps<IInviteMembersValues>): string => {
        const { isValid, values } = formikContext;
        return isValid && values.email !== '' ? '/svg/plus-filled-square.svg' : '/svg/plus-outlined-square.svg';
    };

    const onIconClick = (formikContext: FormikProps<IInviteMembersValues>) => {
        const { isValid, values, setFieldValue } = formikContext;
        if (isValid && values.email !== '' && isUnique(values.email)) {
            setEmails(prev => [values.email, ...prev]);
            setFieldValue('email', '');
        }
    };

    const handleSubmit = () => {
        if (emails.length === 0) return;
        if (!canCreate.canCreate) return;
        getToken(
            {},
            {
                onSuccess: () => {
                    const body: SendInviteEmailsBody = { emails: emails };
                    execSendInviteEmails(organizationId, body);
                    close();
                },
            }
        );
    };

    const context = useFormik<IInviteMembersValues>({
        initialValues: EMPTY_FORM_VALUES,
        onSubmit: handleSubmit,
        validationSchema: INVITE_MEMBERS_VALIDATION(t),
        validateOnMount: true,
        validateOnChange: true,
    });

    return (
        <div className="invite-members">
            <h3 className="invite-members__heading">{content.invitePeople.inviteMembers}</h3>
            <div className="invite-members__email-input-box">
                <div className="invite-members__form">
                    <div className="invite-members__form--input">
                        <span className="invite-members__to-label">To:</span>
                        <InputFormik
                            id="email"
                            className="invite-members__email-input"
                            placeholder={content.invitePeople.enterEmail}
                            type="text"
                            onChange={context.handleChange}
                            context={context}
                            iconSrc={renderIcon(context)}
                            iconAlt="plus square"
                            iconOnClick={() => onIconClick(context)}
                            onKeyDown={e => e.key === 'Enter' && onIconClick(context)}
                        />
                    </div>
                    {emails.length > 0 && (
                        <ul className="invite-members__email-list">
                            {emails.map((_email, j) => (
                                <li className="invite-members__row" key={j}>
                                    <p className="paragraph-4">{_email}</p>
                                    <button type="button" className="invite-members__delete-button" onClick={() => removeEmailFromList(_email)}>
                                        <img src="/svg/notifications/cancel.svg" alt="Delete email from list" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    <div className="invite-members__button-container">
                        <div className="invite-members__copy">
                            <IconButton onClick={copyLink} variant="outline" iconUrl="svg/link.svg" iconAlt="Copy" />
                            <p className="paragraph-4 invite-members__copy-link-label">{message}</p>
                        </div>
                        <div className="premium-tooltip-wrapper">
                            <Button
                                className="invite-members__save-button"
                                disabled={!emails.length || !canCreate.canCreate}
                                onClick={() => context.handleSubmit()}
                                text="Send invite"
                                variant="contained"
                            />
                            {!canCreate.canCreate && <PremiumTooltip showOnHover position="top" />}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InviteMembers;
